/**
 * Checkout Success Page (Story 2.3, Task 4)
 *
 * Polls /api/auth/refresh until the JWT reflects the upgraded plan.
 * Shows plan-specific CTAs after confirmation.
 *
 * Route: /settings/billing/success?session_id=cs_xxx
 * (billing_.success.tsx = TanStack Router pathless layout escape)
 */

import { useState, useEffect, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle } from "@phosphor-icons/react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
} from "@provenleap/ui";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { useAuthStore } from "../../../stores/authStore";
import { LoadingSpinner } from "../../../components/icons";
import { apiGet } from "../../../lib/apiClient";
import { PLAN_DISPLAY_NAMES } from "@provenleap/types";
import type { Plan } from "@provenleap/types";

export const Route = createFileRoute(
  "/_authenticated/settings/billing_/success"
)({
  validateSearch: (search: Record<string, unknown>) => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: CheckoutSuccessPage,
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15; // 30s total

type PaidPlan = Exclude<Plan, "free">;

type CheckoutVerification =
  | { status: "processing" }
  | {
      status: "verified";
      plan: PaidPlan;
      interval: "monthly" | "annual";
    };

function isPaidPlan(value: unknown): value is PaidPlan {
  return value === "starter" || value === "pro" || value === "agency";
}

function parseVerification(value: unknown): CheckoutVerification | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (candidate.status === "processing") return { status: "processing" };
  if (
    candidate.status === "verified" &&
    isPaidPlan(candidate.plan) &&
    (candidate.interval === "monthly" || candidate.interval === "annual")
  ) {
    return {
      status: "verified",
      plan: candidate.plan,
      interval: candidate.interval,
    };
  }
  return null;
}

/** Plan-specific next-step CTAs */
const PLAN_CTAS: Record<
  Exclude<Plan, "free">,
  { label: string; description: string; href: string }
> = {
  starter: {
    label: "Add a Client",
    description: "Start tracking analytics for your first clients.",
    href: "/clients",
  },
  pro: {
    label: "Explore AI Insights",
    description: "Unlock advanced AI-powered recommendations.",
    href: "/dashboard",
  },
  agency: {
    label: "Set Up White-Label Branding",
    description: "Customize reports with your agency's brand.",
    href: "/settings",
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function CheckoutSuccessPage() {
  const { session_id } = Route.useSearch();
  const { plan, refreshPlan } = usePlanLimits();
  const [status, setStatus] = useState<
    "polling" | "success" | "timeout" | "unverified"
  >(
    "polling"
  );
  const [retryGeneration, setRetryGeneration] = useState(0);
  const [verifiedPlan, setVerifiedPlan] = useState<PaidPlan | null>(null);
  const verifiedPlanRef = useRef<PaidPlan | null>(null);
  const verifiedSessionRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!session_id?.startsWith("cs_")) {
      setStatus("unverified");
      return;
    }
    const checkoutSessionId = session_id;

    if (verifiedSessionRef.current !== checkoutSessionId) {
      verifiedSessionRef.current = checkoutSessionId;
      verifiedPlanRef.current = null;
      setVerifiedPlan(null);
    }

    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    function scheduleNextAttempt() {
      if (!cancelled) {
        timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    async function poll() {
      if (cancelled || attempts >= MAX_POLL_ATTEMPTS) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      attempts++;

      let authoritativePlan = verifiedPlanRef.current;
      if (!authoritativePlan) {
        let response: Response;
        try {
          response = await apiGet(
            `/api/billing/checkout/verify?session_id=${encodeURIComponent(checkoutSessionId)}`,
          );
        } catch {
          scheduleNextAttempt();
          return;
        }

        if (!response.ok) {
          if (response.status === 429 || response.status >= 500) {
            scheduleNextAttempt();
          } else if (!cancelled) {
            setStatus("unverified");
          }
          return;
        }

        let payload: unknown;
        try {
          payload = await response.json();
        } catch {
          if (!cancelled) setStatus("unverified");
          return;
        }
        const envelope = payload as { data?: unknown };
        const verification = parseVerification(envelope?.data);
        if (!verification) {
          if (!cancelled) setStatus("unverified");
          return;
        }
        if (verification.status === "processing") {
          scheduleNextAttempt();
          return;
        }

        authoritativePlan = verification.plan;
        verifiedPlanRef.current = verification.plan;
        if (!cancelled) setVerifiedPlan(verification.plan);
      }

      try {
        await refreshPlan();
      } catch {
        scheduleNextAttempt();
        return;
      }

      // After refresh, the Zustand store is updated synchronously.
      // Read the latest plan directly from the store (not from the hook,
      // which won't have re-rendered yet).
      const latestPlan = useAuthStore.getState().user?.plan;

      if (latestPlan === authoritativePlan) {
        if (!cancelled) {
          setStatus("success");
          sessionStorage.removeItem("expectedPlan");
        }
        return;
      }

      scheduleNextAttempt();
    }

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [refreshPlan, retryGeneration, session_id]);

  function handleManualRefresh() {
    setStatus("polling");
    setRetryGeneration((current) => current + 1);
  }

  const upgradedPlan = verifiedPlan ?? plan;
  const cta =
    upgradedPlan !== "free"
      ? PLAN_CTAS[upgradedPlan as Exclude<Plan, "free">]
      : null;

  // =========================================================================
  // Unverified return state
  // =========================================================================
  if (status === "unverified") {
    return (
      <div className="p-6 max-w-[600px] mx-auto" data-testid="success-unverified">
        <Card>
          <CardHeader>
            <CardTitle>We couldn't verify this checkout</CardTitle>
            <CardDescription>
              No plan change has been confirmed on this page. Open Billing to
              check the authoritative subscription status.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/settings/billing" className="block">
              <Button className="w-full">Return to Billing</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =========================================================================
  // Polling state
  // =========================================================================
  if (status === "polling") {
    return (
      <div
        className="p-6 max-w-[600px] mx-auto text-center"
        data-testid="success-polling"
      >
        <LoadingSpinner className="mx-auto mb-4 h-8 w-8" />
        <h1 className="text-2xl font-bold mb-2">Activating your plan...</h1>
        <p className="text-muted-foreground">
          Please wait while we confirm your upgrade.
        </p>
      </div>
    );
  }

  // =========================================================================
  // Timeout state
  // =========================================================================
  if (status === "timeout") {
    return (
      <div className="p-6 max-w-[600px] mx-auto" data-testid="success-timeout">
        <Card>
          <CardHeader>
            <CardTitle>Your upgrade is being processed</CardTitle>
            <CardDescription>
              Features will be available within a few minutes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={handleManualRefresh}
              className="w-full"
              data-testid="refresh-plan-btn"
            >
              Refresh Plan
            </Button>
            <Link to="/dashboard" className="block">
              <Button className="w-full" data-testid="goto-dashboard-timeout">
                Go to Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =========================================================================
  // Success state
  // =========================================================================
  return (
    <div className="p-6 max-w-[600px] mx-auto" data-testid="success-confirmed">
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-3">
            <CheckCircle
              size={48}
              weight="regular"
              className="text-green-600"
              aria-label="Success"
            />
          </div>
          <CardTitle className="text-2xl">
            Welcome to {PLAN_DISPLAY_NAMES[upgradedPlan as Plan] ?? upgradedPlan}!
          </CardTitle>
          <CardDescription>
            Your plan has been upgraded successfully. You can now access all{" "}
            {PLAN_DISPLAY_NAMES[upgradedPlan as Plan] ?? upgradedPlan} features.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Plan-specific CTA */}
          {cta && (
            <Link to={cta.href} className="block" data-testid="plan-cta">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-base">{cta.label}</CardTitle>
                  <CardDescription>{cta.description}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          )}

          {/* Navigation links */}
          <div className="flex flex-col gap-2 pt-2">
            <Link to="/dashboard" className="block">
              <Button className="w-full" data-testid="goto-dashboard">
                Go to Dashboard
              </Button>
            </Link>
            <Link to="/settings/billing" className="block">
              <Button className="w-full" data-testid="goto-billing">
                View Billing
              </Button>
            </Link>
            <Link to="/settings/billing" className="block">
              <Button className="w-full" data-testid="manage-subscription">
                Manage Subscription
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
